import {
  Grid,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Paper,
  Snackbar,
  SnackbarContent,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import React, { useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  login as loginAction,
  setNewPassword as setPasswordAction,
} from "../../actions/auth_actions";
import { AUTH_ERROR_CLEAR } from "../../actions/types";
import * as ROUTES from "../../constants/routes";
import { getBrowserDetail } from "../Common/utils";
import Header from "./Header";

import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getUrl } from "aws-amplify/storage";

const unSupportedMessage = `WARNING: We're very sorry, but the browser you are using is no longer supported. We recommend only using the latest versions of Chrome, Edge, Firefox and Safari before trying to use the Skillsminer Portal.`;
const versionsBehindMessage = `WARNING: The browser you are using is more than 2 versions behind the current version. We always recommend and can only support the latest browser version(s).`;

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const propsErrorMessage = useSelector((state) => state.auth.error);
  const changePassword = useSelector((state) => state.auth.changePassword);
  const currentAuthUser = useSelector((state) => state.auth.currentAuthUser);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [npassword, setNpassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [open, setOpen] = useState(false);
  const [vertical] = useState("top");
  const [horizontal] = useState("center");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [version] = useState(
    (process.env.REACT_APP_REGION === "eu-west-2" ? "uk." : "ireland.") +
    process.env.REACT_APP_STAGE +
    "@" +
    process.env.REACT_APP_VERSION +
    (process.env.REACT_APP_BUILD
      ? "." + parseInt(process.env.REACT_APP_BUILD, 10)
      : "")
  );
  const [manualURL, setManualURL] = useState("");
  const [browserErrorText, setBrowserErrorText] = useState(null);
  const [forceClose, setForceClose] = useState(false);
  const [queryParamsLoaded, setQueryParamsLoaded] = useState(false);
  const [snackbarDuration, setSnackbarDuration] = useState(3000);

  const handleClose = () => {
    setOpen(false);
    dispatch({ type: AUTH_ERROR_CLEAR });
  };

  const handleForceClose = () => {
    setOpen(false);
    setForceClose(true);
  };

  // const fetchManualURL = useCallback(async () => {
  //   getUrl({
  //     path: "public/manuals/portalmanual.pdf",
  //   })
  //     .then(result => setManualURL(result.url.href))
  //     .catch(error => console.error("Error fetching manual URL:", error));
  // }, []);

  const checkBrowserCompatibility = useCallback(() => {
    const [browser, version] = getBrowserDetail().split(" ");
    let message = null;

    switch (browser.toLowerCase()) {
      case "ie":
      case "opera":
        setBrowserErrorText(unSupportedMessage);
        break;
      case "chrome":
        if (parseInt(version) < 76) {
          message = versionsBehindMessage;
        }
        break;
      case "edge":
        if (parseInt(version) < 15) {
          message = versionsBehindMessage;
        }
        break;
      case "safari":
        if (parseInt(version) < 11) {
          message = versionsBehindMessage;
        }
        break;
      case "firefox":
        if (parseInt(version) < 69) {
          message = versionsBehindMessage;
        }
        break;
      default:
        break;
    }
    if (message) {
      setBrowserErrorText(message);
    }
  }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    // fetchManualURL();
    checkBrowserCompatibility();
  }, [checkBrowserCompatibility]);

  useEffect(() => {
    const queryDict = {};
    window.location.search.substr(1).split("&").forEach(function(item) {queryDict[item.split("=")[0]] = item.split("=")[1]});

    if (queryDict.message === "inactivity" && !forceClose) {
      setSnackbarDuration(null); // Keep open until clicked
      setOpen(true);
      setError("Sorry you have been logged out due to inactivity of 60 minutes, please sign in again.");
    } else if (queryDict.email && queryDict.pass && !queryParamsLoaded) {
      setEmail(decodeURIComponent(queryDict.email));
      setPassword(decodeURIComponent(queryDict.pass));
      setQueryParamsLoaded(true);
    }
  }, [forceClose, queryParamsLoaded]);

  useEffect(() => {
    if (propsErrorMessage) {
      //temporary fix bypassing Auth.completeNewPassword device key error
      if (propsErrorMessage === "Invalid device key given.") {
        window.location.reload();
      } else {
        setOpen(true);
        setError(propsErrorMessage);
        setLoading(false);
      }
    }
  }, [propsErrorMessage, dispatch]); // Added dispatch to dependencies

  useEffect(() => {
    setLoading(false);
  }, [changePassword]); // Dependency on changePassword

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const login = (e) => {
    e.preventDefault();

    let username = email.trim();
    let passwordValue = password.trim();

    setLoading(true);
    setError("");
    setOpen(false);

    dispatch(loginAction({ username, password: passwordValue }, navigate));
  };

  const changePass = (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");
    setOpen(false);

    dispatch(setPasswordAction(currentAuthUser, npassword, navigate));
  };

    return (
      <Box>
        <Box sx={{ height: 5 }}>
          {loading && <LinearProgress />}
        </Box>
        <Header />
        {!!browserErrorText ? (
          <Grid
            container
            justifyContent="center"
            alignContent="center"
            alignItems="center"
            sx={{ paddingTop: "1em" }}
          >
            <Grid item size={{xs:4}} sx={{ color: "red", textAlign: "center" }}>
              <div>
                WARNING: We're very sorry, but the browser you are using is no
                longer supported. We recommend only using the latest versions of
                Chrome, Edge, Firefox and Safari before trying to use the
                Skillsminer portal.
              </div>
            </Grid>
          </Grid>
        ) : null}

        <Paper
          sx={{
            mt: 3.75,
            width: 400,
            margin: "auto",
            pt: 2.5,
            pb: 2.5,
          }}
        >
          {!changePassword && (
            <Box component="form" onSubmit={login}>
              <TextField
                id="outlined-email-input"
                label="Email"
                type="email"
                name="email"
                autoComplete="email"
                margin="normal"
                variant="outlined"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ m: 2.5, width: 360 }}
              />
              <TextField
                id="password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                label="Password"
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle password visibility"
                        onClick={handleClickShowPassword}
                      >
                        {showPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                required
                sx={{ m: 2.5, width: 360 }}
              />
              <Button
                variant="contained"
                size="large"
                color="primary"
                type="submit"
                sx={{ m: 2.5, width: 360 }}
              >
                Login
              </Button>
              <Typography variant="body2" sx={{ m: 2.5, mb: 0, mt: 1.25 }}>
                Forgot password?{" "}
                <Link component={RouterLink} to={ROUTES.FORGET}>
                  Reset here
                </Link>
              </Typography>
              {manualURL && (
                <Typography variant="body2" sx={{ m: 2.5, mb: 0, mt: 1.25 }}>
                  <Link
                    href={manualURL}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Download
                  </Link>{" "}
                  Portal User Manual
                </Typography>
              )}
              <Typography variant="body2" sx={{ m: 2.5, mb: 0, mt: 1.25 }}>
                <Link id="youtube-link" href="https://www.youtube.com/watch?v=avXHHVsvvZQ&list=PLaqkddI5TjYUZdQTDb69OSKDadsnpvIx5" target="_blank" rel="noopener noreferrer">
                  Visit
                </Link>
                {" "} our YouTube Channel for Training Videos
              </Typography>
            </Box>
          )}

          {changePassword && (
            <Box component="form" onSubmit={changePass}>
              <Typography variant="subtitle2" sx={{ m: 2.5, mb: 0, mt: 1.25 }}>
                Please set a new password!
              </Typography>
              <TextField
                id="npassword"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                label="New Password"
                autoComplete="new-password"
                value={npassword}
                onChange={(e) => setNpassword(e.target.value)}
                sx={{ m: 2.5, width: 360 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle password visibility"
                        onClick={handleClickShowPassword}
                      >
                        {showPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                required
              />
              <Button
                variant="contained"
                size="large"
                color="primary"
                type="submit"
                sx={{ m: 2.5, width: 360 }}
              >
                Change password
              </Button>
            </Box>
          )}
          <Box sx={{ fontSize: 9, color: "#bbb", textAlign: "right", mr: 1.25 }}>
            {getBrowserDetail()}
          </Box>
          <Box sx={{ fontSize: 9, color: "#bbb", textAlign: "right", mr: 1.25 }}>
            {version}
          </Box>
        </Paper>

        <Snackbar
          anchorOrigin={{ vertical, horizontal }}
          open={open}
          onClose={handleClose}
          onClick={handleForceClose}
          autoHideDuration={snackbarDuration}
          ContentProps={{
            "aria-describedby": "message-id",
          }}
          sx={{ mt: 1.25 }}
        >
          <SnackbarContent
            aria-describedby="client-snackbar"
            message={<span id="message-id">{error}</span>}
            sx={{ backgroundColor: "error.dark" }}
          />
        </Snackbar>
      </Box>
    );
}

export default Login;
